import React, { useState } from 'react';
import { devLog, devError } from '../../../utils/devLogger';
import { BidHistoryItem } from './AuctionDetailPopup';
import {
  BidHistorySection,
  BidHistoryHeader,
  SectionTitle,
  ButtonGroup,
  ActionButton,
  StyledTable,
  ToggleSwitch,
  TooltipCell,
  Tooltip,
  CopyIcon,
  PreBidInfoSection,
  PreBidLabel,
  PreBidUser,
  PreBidPriceKRW,
  PreBidPriceUSD
} from './AuctionDetailStyles';

// PreBid 정보 타입
interface PreBidData {
  index: number;
  auctionIndex: number;
  lotIndex: number;
  price: number;
  isWon: boolean;
  createAt: string;
  updateAt: string | null;
  user: {
    index: number;
    name: string;
  };
}

interface BidHistoryTableProps {
  bidHistory: BidHistoryItem[];
  bidRestrictions: { [key: string]: string };
  bannedUsers?: number[]; // 차단된 사용자 목록 추가
  lotStatus: 'WAITING' | 'ONGOING' | 'COMPLETED'; // 로트 상태 추가
  auctionStartAt?: string; // 경매 시작 시간 추가
  auctionIndex?: number; // ban-user 소켓 이벤트를 위한 auctionIndex 추가
  preBids?: PreBidData[]; // 사전입찰 정보 추가
  currentLotIndex?: number; // 현재 선택된 lot index 추가
  exchangeRate?: number; // 환율 정보 추가
  onExcelDownload: () => void;
  onBidderRegister: () => void;
  onCountStart: () => void;
  // onEndAuction: () => void; // 소켓 카운트다운으로 자동 처리되므로 제거
  onBidRowClick: (bidData: BidHistoryItem) => void;
  onBidRestrictionToggle: (key: string, bidderName: string, currentStatus: string, event: React.MouseEvent) => void;
  onBanUser?: (auctionIndex: number, userIndex: number) => void; // ban-user 소켓 이벤트 핸들러 추가
  onUnbanUser?: (auctionIndex: number, userIndex: number) => void; // unban-user 소켓 이벤트 핸들러 추가
  onStartBid?: () => void; // 개발모드에서만 사용하는 경매시작 핸들러
  // 사전입찰용 옵션들
  isPreBidMode?: boolean; // 사전입찰 모드 여부
  tableTitle?: string; // 테이블 제목 커스터마이징
  hideSiteNumber?: boolean; // 현장번호 컬럼 숨김
  hideBidRestriction?: boolean; // 입찰제한 컬럼 숨김
  hideActionButtons?: boolean; // 액션 버튼들 숨김 (엑셀 다운로드 제외)
}

// 툴팁과 복사 기능이 있는 셀 컴포넌트
const CopyableCell: React.FC<{ content: string; showTooltip?: boolean }> = ({ 
  content, 
  showTooltip = true 
}) => {
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const cellRef = React.useRef<HTMLDivElement>(null);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    // 기존 타이머 정리
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    if (cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      setTooltipStyle({
        left: rect.left + (rect.width / 2),
        top: rect.top - 15, // 셀 위쪽으로 (여유 공간 추가)
        transform: 'translate(-50%, -100%)',
      });
    }
    setShowTooltipState(true);
  };

  const handleMouseLeave = () => {
    // 지연시간을 두고 툴팁 숨김 (툴팁으로 마우스 이동할 시간 확보)
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltipState(false);
    }, 200);
  };

  const handleTooltipMouseEnter = () => {
    // 툴팁에 마우스가 올라가면 숨김 타이머 취소
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    // 툴팁에서 마우스가 벗어나면 즉시 숨김
    setShowTooltipState(false);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      devError('복사 실패:', err);
    }
  };

  // 텍스트가 말줄임표로 잘릴지 확인하는 함수
  const isTextTruncated = () => {
    if (!cellRef.current) return false;
    const span = cellRef.current.querySelector('span');
    if (!span) return false;
    return span.scrollWidth > span.clientWidth;
  };

  // 컴포넌트 언마운트 시 타이머 정리
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <TooltipCell
      ref={cellRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span>{content}</span>
      {showTooltip && showTooltipState && content && content !== '-' && isTextTruncated() && (
        <Tooltip 
          style={tooltipStyle}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{content}</span>
            <CopyIcon onClick={handleCopy} title={copied ? '복사됨!' : '복사'}>
              {copied ? '✓' : '📋'}
            </CopyIcon>
          </div>
        </Tooltip>
      )}
    </TooltipCell>
  );
};

const BidHistoryTable: React.FC<BidHistoryTableProps> = ({
  bidHistory,
  bidRestrictions,
  bannedUsers = [], // 차단된 사용자 목록
  lotStatus,
  auctionStartAt,
  auctionIndex,
  preBids = [],
  currentLotIndex,
  exchangeRate = 1,
  onExcelDownload,
  onBidderRegister,
  onCountStart,
  onBidRowClick,
  onBidRestrictionToggle,
  onBanUser,
  onUnbanUser,
  onStartBid,
  // 사전입찰용 옵션들
  isPreBidMode = false,
  tableTitle,
  hideSiteNumber = false,
  hideBidRestriction = false,
  hideActionButtons = false
}) => {
  // 개발모드 감지
  const isDebugMode = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
  
  // 개발 모드에서 차단된 사용자와 입찰 내역 비교 디버깅
  if (isDebugMode) {
    devLog('🔍 [BidHistoryTable] 차단된 사용자와 입찰 내역 비교:', {
      bannedUsers,
      bidHistoryCount: bidHistory.length,
      bidHistoryWithUsers: bidHistory.filter(bid => bid.user && bid.user.index !== undefined).map(bid => ({
        bidder: bid.bidder,
        userIndex: bid.user?.index,
        isBanned: bid.user && bid.user.index !== undefined ? bannedUsers.includes(bid.user.index) : false
      }))
    });
  }

  // bannedUsers 변경 시 추가 디버깅
  React.useEffect(() => {
    if (isDebugMode && bannedUsers.length > 0) {
      devLog('🚫 [BidHistoryTable] bannedUsers 상태 변경 감지:', {
        bannedUsers,
        bidHistoryUsers: bidHistory.map(bid => ({
          bidder: bid.bidder,
          userIndex: bid.user?.index,
          willBeRestricted: bid.user && bid.user.index !== undefined ? bannedUsers.includes(bid.user.index) : false
        }))
      });
    }
  }, [bannedUsers, bidHistory, isDebugMode]);
  
  // 경매종료 상태인지 확인
  const isAuctionCompleted = lotStatus === 'COMPLETED';
  
  // 라이브 경매가 시작되기 전인지 확인
  const isBeforeAuctionStart = auctionStartAt ? new Date() < new Date(auctionStartAt) : false;
  
  // 버튼이 비활성화되어야 하는 조건
  // 라이브가 시작되고, 현재 진행중인 lot만 버튼이 활성화되어야 함
  const shouldDisableButtons = isAuctionCompleted || isBeforeAuctionStart || lotStatus !== 'ONGOING';

  // 입찰제한 토글 핸들러 (모달을 통한 확인 후 처리)
  const handleBidRestrictionToggle = (bidItem: BidHistoryItem, event: React.MouseEvent) => {
    event.stopPropagation(); // 행 클릭 이벤트 방지
    
    // 입찰제한 상태 확인: bannedUsers 목록을 우선적으로 확인
    const isRestricted = bidItem.user && bidItem.user.index !== undefined 
      ? bannedUsers.includes(bidItem.user.index) 
      : bidRestrictions[bidItem.key] === 'Y';
    
    const currentStatus = isRestricted ? 'Y' : 'N';
    const bidderName = bidItem.bidder;
    
    if (isDebugMode) {
      devLog('🚫 [BidHistoryTable] 입찰제한 토글:', {
        bidder: bidderName,
        currentRestricted: isRestricted,
        currentStatus,
        willBeRestricted: !isRestricted,
        auctionIndex,
        bidItem,
        hasUser: !!bidItem.user,
        userIndex: bidItem.user?.index
      });
    }
    
    // 사전입찰자는 차단할 수 없음 (user 정보가 없음)
    if (!bidItem.user) {
      devLog('⚠️ [BidHistoryTable] 사전입찰자는 차단할 수 없습니다:', {
        bidder: bidderName,
        isPreBidder: bidItem.isPreBidder
      });
      alert('사전입찰자는 차단할 수 없습니다.');
      return;
    }
    
    // 기존 onBidRestrictionToggle 콜백 호출 (모달을 띄우기 위해) - currentStatus 추가
    if (onBidRestrictionToggle) {
      onBidRestrictionToggle(bidItem.key, bidderName, currentStatus, event);
    }
  };
  
  // 개발 모드에서 상태 확인용 로그
  if (isDebugMode) {
    devLog('🔘 [BidHistoryTable] 버튼 상태 확인:', {
      lotStatus,
      auctionStartAt,
      isAuctionCompleted,
      isBeforeAuctionStart,
      shouldDisableButtons,
      isOngoing: lotStatus === 'ONGOING',
      currentTime: new Date().toISOString()
    });
  }

  // 현재 로트의 사전입찰 정보 중 최고가 찾기
  const currentLotPreBids = currentLotIndex 
    ? preBids.filter(pb => pb.lotIndex === currentLotIndex)
    : [];
  
  const highestPreBid = currentLotPreBids.length > 0 
    ? currentLotPreBids.reduce((highest, current) => 
        current.price > highest.price ? current : highest
      )
    : null;

  // 디버깅을 위한 로그 추가
  if (isDebugMode) {
    devLog('🎯 [BidHistoryTable] PreBids 디버깅 정보:', {
      currentLotIndex,
      preBidsLength: preBids.length,
      preBids: preBids,
      currentLotPreBids: currentLotPreBids,
      highestPreBid: highestPreBid,
      exchangeRate
    });
  }

  // 가격 포매팅 함수
  const formatPrice = (price: number, currency: 'KRW' | 'USD' = 'KRW') => {
    if (currency === 'KRW') {
      return price.toLocaleString('ko-KR');
    } else {
      return (price / exchangeRate).toFixed(2);
    }
  };

  if (isDebugMode && highestPreBid) {
    devLog('🎯 [BidHistoryTable] 사전입찰 최고가 정보:', {
      currentLotIndex,
      totalPreBids: preBids.length,
      currentLotPreBids: currentLotPreBids.length,
      highestPreBid,
      formattedPriceKRW: formatPrice(highestPreBid.price, 'KRW'),
      formattedPriceUSD: formatPrice(highestPreBid.price, 'USD')
    });
  }
  return (
    <BidHistorySection>
      <BidHistoryHeader>
        <SectionTitle>{tableTitle || (isPreBidMode ? '사전입찰내역' : '입찰내역')}</SectionTitle>
        <ButtonGroup>
          <ActionButton variant="excel" onClick={onExcelDownload}>
            엑셀다운로드
          </ActionButton>
          {!hideActionButtons && (
            <>
              {/* 개발모드에서만 보이는 경매시작 버튼 */}
              {/* {isDebugMode && onStartBid && (
                <ActionButton 
                  onClick={shouldDisableButtons ? undefined : onStartBid}
                  disabled={shouldDisableButtons}
                  title={shouldDisableButtons ? 
                    (isAuctionCompleted ? '경매가 종료되었습니다' : '라이브 경매 시작 전입니다') : 
                    undefined}
                >
                  경매시작
                </ActionButton>
              )} */}
              <ActionButton 
                onClick={shouldDisableButtons ? undefined : onBidderRegister}
                disabled={shouldDisableButtons}
                title={shouldDisableButtons ? 
                  (isAuctionCompleted ? '경매가 종료되었습니다' : 
                   isBeforeAuctionStart ? '라이브 경매 시작 전입니다' : 
                   lotStatus === 'WAITING' ? '대기 중인 LOT입니다. 진행 중인 LOT에서만 사용 가능합니다' :
                   '현재 진행 중인 LOT에서만 사용 가능합니다') : 
                  undefined}
              >
                현장입찰 등록
              </ActionButton>
              <ActionButton 
                onClick={shouldDisableButtons ? undefined : onCountStart}
                disabled={shouldDisableButtons}
                title={shouldDisableButtons ? 
                  (isAuctionCompleted ? '경매가 종료되었습니다' : 
                   isBeforeAuctionStart ? '라이브 경매 시작 전입니다' : 
                   lotStatus === 'WAITING' ? '대기 중인 LOT입니다. 진행 중인 LOT에서만 사용 가능합니다' :
                   '현재 진행 중인 LOT에서만 사용 가능합니다') : 
                  undefined}
              >
                20초 카운트
              </ActionButton>
              {/* <ActionButton onClick={onEndAuction}>
                경매 종료
              </ActionButton> */}
            </>
          )}
        </ButtonGroup>
      </BidHistoryHeader>
      
      {/* 사전입찰 최고가 정보 표시 */}
      {highestPreBid && (
        <PreBidInfoSection>
          <PreBidLabel>사전입찰 최고가</PreBidLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PreBidUser>{highestPreBid.user.name}</PreBidUser>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
              <PreBidPriceKRW>₩{formatPrice(highestPreBid.price, 'KRW')}</PreBidPriceKRW>
              <PreBidPriceUSD>${formatPrice(highestPreBid.price, 'USD')}</PreBidPriceUSD>
            </div>
          </div>
        </PreBidInfoSection>
      )}
      <StyledTable>
        <thead>
          <tr>
            <th>No</th>
            <th>{isPreBidMode ? '사전입찰일시' : '입찰일시'}</th>
            <th>입찰자</th>
            {!hideSiteNumber && <th>현장번호</th>}
            <th>국가</th>
            <th>입찰금액(KRW)</th>
            <th>입찰금액(USD)</th>
            <th>연락처</th>
            <th>이메일</th>
            {!hideBidRestriction && <th>입찰제한</th>}
          </tr>
        </thead>
        <tbody>
          {bidHistory.map((row, index) => {
            const isClickable = row.siteNumber && row.siteNumber.trim() !== '';
            
            // 입찰제한 상태 확인: bannedUsers 목록을 우선적으로 확인
            const isRestricted = row.user && row.user.index !== undefined 
              ? bannedUsers.includes(row.user.index) 
              : bidRestrictions[row.key] === 'Y';
            
            // 개발 모드에서 입찰제한 상태 디버깅
            if (isDebugMode && row.user && row.user.index !== undefined) {
              devLog(`🔍 [BidHistoryTable] 입찰자 ${row.bidder} (${row.user.index}) 제한 상태:`, {
                userIndex: row.user.index,
                bannedUsers,
                isBannedInList: bannedUsers.includes(row.user.index),
                bidRestrictionValue: bidRestrictions[row.key],
                finalIsRestricted: isRestricted
              });
            }
            
            return (
              <tr 
                key={row.key} 
                className={isClickable ? 'clickable' : 'non-clickable'}
                onClick={isClickable ? () => onBidRowClick(row) : undefined}
                style={row.isWon ? { backgroundColor: 'rgba(255, 161, 161, 0.3)' } : undefined}
              >
                <td>{row.no}</td>
                <td><CopyableCell content={row.bidTime} /></td>
                <td><CopyableCell content={row.bidder} /></td>
                {!hideSiteNumber && <td><CopyableCell content={row.siteNumber || '-'} /></td>}
                <td><CopyableCell content={row.country} /></td>
                <td><CopyableCell content={row.bidAmountKrw} /></td>
                <td><CopyableCell content={row.bidAmountUsd} /></td>
                <td><CopyableCell content={row.contact || '-'} /></td>
                <td><CopyableCell content={row.email || '-'} /></td>
                {!hideBidRestriction && (
                  <td>
                    {row.isPreBidder ? (
                      <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                    ) : (
                      <ToggleSwitch
                        className={isRestricted ? 'active' : ''}
                        onClick={(e) => handleBidRestrictionToggle(row, e)}
                      />
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          {bidHistory.length === 0 && (
            <tr>
              <td colSpan={hideSiteNumber && hideBidRestriction ? 8 : hideSiteNumber || hideBidRestriction ? 9 : 10} style={{ textAlign: 'center', padding: '20px' }}>
                {isPreBidMode ? '사전입찰 내역이 없습니다.' : '입찰 내역이 없습니다.'}
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </BidHistorySection>
  );
};

export default BidHistoryTable;
